import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Copy, Download, Printer, Link as LinkIcon } from "lucide-react";
import { partnerApi } from "@/lib/partner";
import { useLocale } from "@/i18n/useLocale";
import { PartnerShell, PartnerLoading, PartnerError } from "@/components/partner/PartnerShell";
import promoEn from "@/data/partner_promo_content.en.json";
import promoAr from "@/data/partner_promo_content.ar.json";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

const TAB_ORDER = ["referral", "swipes", "banners", "faq", "onePager"];

export default function PartnerMarketing() {
  const { locale } = useLocale();
  const isAr = String(locale || "en").toLowerCase().startsWith("ar");
  const content = (isAr ? promoAr : promoEn).marketing;

  const [marketing, setMarketing] = useState(null);
  const [kit, setKit] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [tab, setTab] = useState("referral");

  const load = useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const [link, k] = await Promise.all([
        partnerApi.marketingLink(),
        partnerApi.marketingKit().catch(() => ({ items: [] })),
      ]);
      setMarketing(link || null);
      setKit(Array.isArray(k?.items) ? k.items : []);
    } catch (e) {
      setErr(e?.response?.data?.detail || "load_failed");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const copy = (val, msg) => {
    if (!val) return;
    navigator.clipboard.writeText(val);
    toast.success(msg || "Copied to clipboard");
  };

  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const absoluteUrl = (path) => (path?.startsWith("http") ? path : `${origin}${path || ""}`);

  const swipes = useMemo(
    () => content.swipes.map((t) => ({ ...t, filled: t.body.replaceAll("{LINK}", marketing?.link || "{LINK}") })),
    [content.swipes, marketing?.link]
  );

  if (loading) {
    return (
      <PartnerShell testId="partner-marketing-page">
        <h1 className="text-3xl font-light tracking-tight">{isAr ? "المواد التسويقية" : "Marketing kit"}</h1>
        <PartnerLoading testId="partner-marketing-loading" />
      </PartnerShell>
    );
  }

  if (err) {
    const isGate = err === "not_a_partner";
    return (
      <PartnerShell testId="partner-marketing-page">
        <h1 className="text-3xl font-light tracking-tight">{isAr ? "المواد التسويقية" : "Marketing kit"}</h1>
        <PartnerError
          testId="partner-marketing-error"
          message={isGate ? (isAr ? "لست شريكًا بعد." : "You're not a partner yet.") : (isAr ? "تعذّر تحميل المواد التسويقية." : "We couldn't load your marketing kit.")}
          onRetry={isGate ? undefined : load}
        />
      </PartnerShell>
    );
  }

  return (
    <PartnerShell testId="partner-marketing-page">
      <h1 className="text-3xl font-light tracking-tight">{isAr ? "المواد التسويقية" : "Marketing kit"}</h1>
      <p className="mt-2 text-sm text-white/60">
        {isAr ? "كل ما تحتاجه للترويج لـ TapPresence لشبكتك." : "Everything you need to promote TapPresence to your network."}
      </p>

      {/* Tabs */}
      <div className="mt-6 flex flex-wrap items-center gap-2 border-b border-white/8 pb-3" role="tablist" data-testid="partner-marketing-tabs">
        {TAB_ORDER.map((t) => (
          <button
            key={t}
            role="tab"
            aria-selected={tab === t}
            onClick={() => setTab(t)}
            data-testid={`partner-marketing-tab-${t}`}
            className={`rounded-full border px-3.5 py-1.5 text-xs transition-colors ${tab === t
              ? "border-[#D6A653] bg-[#D6A653]/12 text-[#D6A653]"
              : "border-white/12 text-white/60 hover:text-white"}`}
          >
            {content.tabs[t]}
          </button>
        ))}
      </div>

      {/* --- REFERRAL TAB --- */}
      {tab === "referral" && (
        <section className="mt-6" data-testid="partner-marketing-panel-referral">
          <div className="rounded-2xl border border-white/10 bg-[#0B0D12] p-5" data-testid="marketing-refblock">
            <p className="text-xs uppercase tracking-wider text-white/45">
              {isAr ? "رابط الإحالة الخاص بك" : "Your referral link"}
            </p>
            {marketing?.link ? (
              <>
                <code className="mt-2 block truncate rounded-lg border border-white/10 bg-black/40 px-3 py-2 text-xs text-white/70" data-testid="marketing-link">
                  {marketing.link}
                </code>
                <div className="mt-3 flex flex-wrap items-center gap-3">
                  <button
                    onClick={() => copy(marketing.link)}
                    data-testid="copy-link"
                    className="inline-flex items-center gap-1 rounded-lg border border-[#D6A653]/50 bg-[#D6A653]/10 px-3 py-1.5 text-xs text-[#D6A653]"
                  >
                    <Copy className="h-3 w-3" /> {isAr ? "نسخ الرابط" : "Copy link"}
                  </button>
                  {marketing?.qr_url ? (
                    <a
                      href={`${BACKEND}${marketing.qr_url}`}
                      target="_blank"
                      rel="noreferrer"
                      className="text-xs text-white/50 underline"
                      data-testid="marketing-qr-download"
                    >
                      {isAr ? "تحميل رمز QR" : "Download QR"}
                    </a>
                  ) : null}
                </div>
                {marketing?.qr_url ? (
                  <img
                    src={`${BACKEND}${marketing.qr_url}`}
                    alt="Referral QR"
                    className="mt-4 h-32 w-32 rounded-lg border border-white/10 bg-white/[0.02]"
                  />
                ) : null}
              </>
            ) : (
              <p className="mt-2 text-sm text-white/50">
                {isAr ? "سيظهر رابط الإحالة الخاص بك بعد تفعيل حسابك." : "Your referral link will appear once your Partner account is active."}
              </p>
            )}
          </div>

          {/* Admin-curated kit downloads, if any */}
          {kit.length > 0 && (
            <div className="mt-8">
              <h2 className="text-sm font-medium text-white/80">{isAr ? "أصول للتنزيل" : "Downloads"}</h2>
              <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
                {kit.map((a) => (
                  <a
                    key={a?.id}
                    href={a?.url}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`asset-${a?.id}`}
                    className="rounded-2xl border border-white/10 bg-[#0B0D12] p-4 transition-colors hover:border-[#D6A653]/40"
                  >
                    <p className="text-sm font-medium text-white">{a?.name}</p>
                    {a?.description ? <p className="mt-1 text-xs text-white/50">{a.description}</p> : null}
                    <p className="mt-2 text-[11px] text-white/40">
                      {(a?.mime_type || "file").split("/").pop().toUpperCase()}
                      {a?.size_bytes ? ` · ${Math.round(a.size_bytes / 1024)} KB` : ""}
                    </p>
                  </a>
                ))}
              </div>
            </div>
          )}
        </section>
      )}

      {/* --- SWIPES TAB --- */}
      {tab === "swipes" && (
        <section className="mt-6" data-testid="partner-marketing-panel-swipes">
          <p className="text-xs text-white/55">{content.swipesIntro}</p>
          <div className="mt-4 space-y-3">
            {swipes.map((s) => (
              <div key={s.id} className="rounded-2xl border border-white/10 bg-[#0B0D12] p-4" data-testid={`swipe-${s.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <p className="text-sm font-medium text-white">{s.label}</p>
                  <button
                    onClick={() => copy(s.filled)}
                    data-testid={`swipe-copy-${s.id}`}
                    className="inline-flex items-center gap-1 rounded-lg border border-white/15 px-2.5 py-1 text-xs text-white/70 hover:bg-white/5"
                  >
                    <Copy className="h-3 w-3" /> {isAr ? "نسخ" : "Copy"}
                  </button>
                </div>
                <pre className="mt-2 whitespace-pre-wrap rounded-lg border border-white/10 bg-black/40 px-3 py-2 text-xs text-white/75">{s.filled}</pre>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* --- BANNERS TAB --- */}
      {tab === "banners" && (
        <section className="mt-6" data-testid="partner-marketing-panel-banners">
          <p className="text-xs text-white/55">{content.bannersIntro}</p>
          <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
            {content.banners.map((b) => (
              <div key={b.id} className="rounded-2xl border border-white/10 bg-[#0B0D12] p-4" data-testid={`banner-${b.id}`}>
                <div className={`overflow-hidden rounded-xl border border-white/10 bg-white/[0.02] ${b.aspect}`}>
                  <img src={b.src} alt={b.label} loading="lazy" className="h-full w-full object-cover" />
                </div>
                <div className="mt-3 flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-white">{b.label}</p>
                    <p className="mt-0.5 text-[11px] text-white/45">{b.dimsNote}</p>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <button
                      onClick={() => copy(absoluteUrl(b.src), content.bannerUrlCopied)}
                      data-testid={`banner-copy-${b.id}`}
                      className="inline-flex items-center gap-1 rounded-lg border border-white/15 px-2.5 py-1 text-xs text-white/70 hover:bg-white/5"
                    >
                      <LinkIcon className="h-3 w-3" /> {content.bannerCopyUrl}
                    </button>
                    <a
                      href={b.src}
                      download={b.downloadName}
                      data-testid={`banner-download-${b.id}`}
                      className="inline-flex items-center gap-1 rounded-lg border border-[#D6A653]/50 bg-[#D6A653]/10 px-2.5 py-1 text-xs text-[#D6A653]"
                    >
                      <Download className="h-3 w-3" /> {content.bannerDownload}
                    </a>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* --- FAQ TAB --- */}
      {tab === "faq" && (
        <section className="mt-6" data-testid="partner-marketing-panel-faq">
          <p className="text-xs text-white/55">{content.faqIntro}</p>
          <div className="mt-4 space-y-3">
            {content.faq.map((row, i) => (
              <details
                key={i}
                className="group rounded-2xl border border-white/10 bg-[#0B0D12] p-4 open:border-[#D6A653]/30"
                data-testid={`faq-${i}`}
              >
                <summary className="cursor-pointer list-none text-sm font-medium text-white/85 marker:content-['']">
                  {row.q}
                </summary>
                <p className="mt-2 text-sm leading-relaxed text-white/65">{row.a}</p>
              </details>
            ))}
          </div>
        </section>
      )}

      {/* --- ONE-PAGER TAB --- */}
      {tab === "onePager" && (
        <section className="mt-6" data-testid="partner-marketing-panel-onePager">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-xs text-white/55">{content.onePagerIntro}</p>
            <button
              onClick={() => window.print()}
              data-testid="onepager-print"
              className="inline-flex items-center gap-1 rounded-full border border-[#D6A653]/50 bg-[#D6A653]/10 px-3 py-1.5 text-xs text-[#D6A653]"
            >
              <Printer className="h-3 w-3" /> {content.onePagerPrintCta}
            </button>
          </div>

          <div id="onepager-printable" className="mt-6 rounded-2xl border border-white/10 bg-white/[0.02] p-8 text-white" data-testid="partner-onepager">
            <h2 className="text-3xl font-light tracking-tight text-white">{content.onePager.title}</h2>
            <p className="mt-2 max-w-2xl text-sm text-white/70">{content.onePager.subtitle}</p>

            <div className="mt-6 grid grid-cols-2 gap-3 md:grid-cols-4">
              {content.onePager.highlights.map((h, i) => (
                <div key={i} className="rounded-xl border border-white/10 bg-black/20 p-3">
                  <p className="text-2xl font-light text-[#D6A653]">{h.kicker}</p>
                  <p className="mt-1 text-[11px] leading-relaxed text-white/70">{h.label}</p>
                </div>
              ))}
            </div>

            <h3 className="mt-8 text-sm font-medium uppercase tracking-wider text-white/50">{content.onePager.howTitle}</h3>
            <ol className={`mt-3 space-y-1.5 text-sm text-white/75 ${isAr ? "list-decimal pr-5" : "list-decimal pl-5"}`}>
              {content.onePager.how.map((s, i) => (<li key={i}>{s}</li>))}
            </ol>

            <h3 className="mt-8 text-sm font-medium uppercase tracking-wider text-white/50">{content.onePager.productTitle}</h3>
            <p className="mt-3 text-sm leading-relaxed text-white/75">{content.onePager.product}</p>

            <p className="mt-8 text-xs text-white/50">
              partners@tappresence.com · tappresence.com/partners
            </p>
          </div>

          {/* Print styles — hide app chrome so Print-to-PDF renders only the one-pager card. */}
          <style>{`
            @media print {
              body * { visibility: hidden !important; }
              #onepager-printable, #onepager-printable * { visibility: visible !important; }
              #onepager-printable {
                position: absolute; inset: 0;
                background: #ffffff !important;
                color: #0B0B0C !important;
                border: none !important;
                padding: 32px !important;
              }
              #onepager-printable * {
                color: #0B0B0C !important;
                background: transparent !important;
                border-color: rgba(0,0,0,0.15) !important;
              }
              #onepager-printable .text-\\[\\#D6A653\\] { color: #8a6a2f !important; }
            }
          `}</style>
        </section>
      )}
    </PartnerShell>
  );
}
